import { create } from "zustand";

import type {
  CNSSourceCredential,
  CNSSourceCredentialStatus,
  CNSSourceProvider,
} from "@/models/sourceConnection";
import {
  ensureSourceCredential as ensureSourceCredentialRequest,
  fetchSourceCredentials as fetchSourceCredentialsRequest,
  fetchSourceProviders as fetchSourceProvidersRequest,
} from "@/services/sourceCredentialService";

export const EMPTY_SOURCE_PROVIDERS: CNSSourceProvider[] = [];
export const EMPTY_SOURCE_CREDENTIALS: CNSSourceCredential[] = [];

const providerInflight = new Map<string, Promise<CNSSourceProvider[]>>();
const credentialInflight = new Map<string, Promise<CNSSourceCredential[]>>();
let storeGeneration = 0;

interface SourceCredentialPatch {
  credentialPublicId?: string | null;
  workspacePublicId?: string | null;
  status?: CNSSourceCredentialStatus | null;
  errorCode?: string | null;
  displayAccount?: string | null;
}

interface SourceCredentialStore {
  providers: CNSSourceProvider[] | null;
  credentialsByWorkspaceId: Record<string, CNSSourceCredential[]>;
  loadingProviders: boolean;
  loadingByWorkspaceId: Record<string, boolean>;
  fetchProviders: (options?: { force?: boolean }) => Promise<CNSSourceProvider[]>;
  fetchCredentials: (
    workspaceId: string,
    options?: { force?: boolean },
  ) => Promise<CNSSourceCredential[]>;
  ensureCredential: (workspaceId: string, provider: string) => Promise<CNSSourceCredential>;
  upsertCredential: (credential: CNSSourceCredential) => void;
  applyCredentialPatch: (patch: SourceCredentialPatch) => void;
  refreshLoadedWorkspaces: () => Promise<void>;
  reset: () => void;
}

export const useSourceCredentialStore = create<SourceCredentialStore>((set, get) => ({
  providers: null,
  credentialsByWorkspaceId: {},
  loadingProviders: false,
  loadingByWorkspaceId: {},

  fetchProviders: async (options) => {
    const cached = get().providers;
    if (cached && !options?.force) return cached;
    const existing = providerInflight.get("providers");
    if (existing) return existing;

    const generation = storeGeneration;
    set({ loadingProviders: true });
    const request = fetchSourceProvidersRequest()
      .then((providers) => {
        if (generation === storeGeneration) {
          set({ providers, loadingProviders: false });
        }
        return providers;
      })
      .catch((error) => {
        if (generation === storeGeneration) {
          set({ loadingProviders: false });
        }
        throw error;
      })
      .finally(() => {
        if (providerInflight.get("providers") === request) {
          providerInflight.delete("providers");
        }
      });
    providerInflight.set("providers", request);
    return request;
  },

  fetchCredentials: async (workspaceId, options) => {
    const cached = get().credentialsByWorkspaceId[workspaceId];
    if (cached && !options?.force) return cached;
    const existing = credentialInflight.get(workspaceId);
    if (existing) return existing;

    const generation = storeGeneration;
    set((state) => ({
      loadingByWorkspaceId: {
        ...state.loadingByWorkspaceId,
        [workspaceId]: true,
      },
    }));
    const request = fetchSourceCredentialsRequest(workspaceId)
      .then((credentials) => {
        if (generation === storeGeneration) {
          set((state) => ({
            credentialsByWorkspaceId: {
              ...state.credentialsByWorkspaceId,
              [workspaceId]: credentials,
            },
            loadingByWorkspaceId: {
              ...state.loadingByWorkspaceId,
              [workspaceId]: false,
            },
          }));
        }
        return credentials;
      })
      .catch((error) => {
        if (generation === storeGeneration) {
          set((state) => ({
            loadingByWorkspaceId: {
              ...state.loadingByWorkspaceId,
              [workspaceId]: false,
            },
          }));
        }
        throw error;
      })
      .finally(() => {
        if (credentialInflight.get(workspaceId) === request) {
          credentialInflight.delete(workspaceId);
        }
      });
    credentialInflight.set(workspaceId, request);
    return request;
  },

  ensureCredential: async (workspaceId, provider) => {
    const generation = storeGeneration;
    const credential = await ensureSourceCredentialRequest(workspaceId, provider);
    if (generation === storeGeneration) {
      get().upsertCredential(credential);
    }
    return credential;
  },

  upsertCredential: (credential) => {
    set((state) => {
      const current =
        state.credentialsByWorkspaceId[credential.workspacePublicId] ??
        EMPTY_SOURCE_CREDENTIALS;
      const next = current.filter((row) => row.id !== credential.id);
      next.push(credential);
      return {
        credentialsByWorkspaceId: {
          ...state.credentialsByWorkspaceId,
          [credential.workspacePublicId]: next,
        },
      };
    });
  },

  applyCredentialPatch: (patch) => {
    if (!patch.credentialPublicId) return;
    set((state) => {
      let changed = false;
      const credentialsByWorkspaceId = { ...state.credentialsByWorkspaceId };
      for (const [workspaceId, credentials] of Object.entries(
        state.credentialsByWorkspaceId,
      )) {
        if (patch.workspacePublicId && workspaceId !== patch.workspacePublicId) continue;
        const next = credentials.map((credential) => {
          if (credential.id !== patch.credentialPublicId) return credential;
          changed = true;
          return {
            ...credential,
            ...(patch.status ? { status: patch.status } : {}),
            ...(patch.errorCode !== undefined
              ? {
                  lastErrorCode:
                    patch.errorCode as CNSSourceCredential["lastErrorCode"],
                }
              : {}),
            ...(patch.displayAccount !== undefined
              ? { displayAccount: patch.displayAccount }
              : {}),
          };
        });
        credentialsByWorkspaceId[workspaceId] = next;
      }
      return changed ? { credentialsByWorkspaceId } : state;
    });
  },

  refreshLoadedWorkspaces: async () => {
    const workspaceIds = Object.keys(get().credentialsByWorkspaceId);
    await Promise.allSettled(
      workspaceIds.map((workspaceId) =>
        get().fetchCredentials(workspaceId, { force: true }),
      ),
    );
  },

  reset: () => {
    storeGeneration += 1;
    providerInflight.clear();
    credentialInflight.clear();
    set({
      providers: null,
      credentialsByWorkspaceId: {},
      loadingProviders: false,
      loadingByWorkspaceId: {},
    });
  },
}));

/** Selects cached credentials for a workspace with a stable empty fallback. */
export function selectSourceCredentials(
  state: SourceCredentialStore,
  workspaceId: string | null,
): CNSSourceCredential[] {
  return workspaceId
    ? state.credentialsByWorkspaceId[workspaceId] ?? EMPTY_SOURCE_CREDENTIALS
    : EMPTY_SOURCE_CREDENTIALS;
}
